import { useState, useEffect } from 'react';
import { UserManagement } from './UserManagement';
import { Dashboard } from './Dashboard';
import { CertificateManagement } from './CertificateManagement';
import { MtlsLayout } from './mtls/MtlsLayout';
import IamLayout from './iam/IamLayout';

interface AdminLTELayoutProps {
  children: React.ReactNode;
  userName: string;
  onSignOut: () => void;
}

export function AdminLTELayout({ children, userName, onSignOut }: AdminLTELayoutProps) {
  const [activeMenu, setActiveMenu] = useState('workloads');

  useEffect(() => {
    // Initialize AdminLTE without state persistence
    const script = document.createElement('script');
    script.innerHTML = `
      $(document).ready(function() {
        // Clear any AdminLTE stored state
        localStorage.removeItem('AdminLTE:Layout:sidebar-collapse');
        localStorage.removeItem('AdminLTE:Layout:sidebar-open');
        
        // Initialize AdminLTE with state persistence disabled
        if (typeof $.fn.Layout !== 'undefined') {
          $('body').Layout({
            animationSpeed: 300,
            sidebarPushMenu: true,
            sidebarExpandOnHover: false,
            enableRemember: false  // Disable AdminLTE's state persistence
          });
        }
        
        // On mobile, ensure sidebar starts collapsed
        if (window.innerWidth < 768) {
          $('body').addClass('sidebar-collapse').removeClass('sidebar-open');
        }
      });
    `;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <div className="wrapper">
      {/* Navbar */}
      <nav className="main-header navbar navbar-expand navbar-white navbar-light">
        {/* Left navbar links */}
        <ul className="navbar-nav">
          <li className="nav-item">
            <a className="nav-link" data-widget="pushmenu" href="#" role="button">
              <i className="fas fa-bars"></i>
            </a>
          </li>
          <li className="nav-item d-none d-sm-inline-block">
            <span className="nav-link">PAT - Platform Administration Tool</span>
          </li>
        </ul>

        {/* Right navbar links */}
        <ul className="navbar-nav ml-auto">
          {/* Notifications Dropdown Menu */}
          <li className="nav-item dropdown">
            <a className="nav-link" data-toggle="dropdown" href="#">
              <i className="far fa-bell"></i>
              <span className="badge badge-warning navbar-badge">3</span>
            </a>
            <div className="dropdown-menu dropdown-menu-lg dropdown-menu-right">
              <span className="dropdown-item dropdown-header">3 Notifications</span>
              <div className="dropdown-divider"></div>
              <a href="#" className="dropdown-item">
                <i className="fas fa-check mr-2"></i> Workload deployed
                <span className="float-right text-muted text-sm">5 mins</span>
              </a>
              <div className="dropdown-divider"></div>
              <a href="#" className="dropdown-item">
                <i className="fas fa-exclamation-triangle mr-2"></i> Pipeline failed
                <span className="float-right text-muted text-sm">2 hours</span>
              </a>
              <div className="dropdown-divider"></div>
              <a href="#" className="dropdown-item">
                <i className="fas fa-info-circle mr-2"></i> New account created
                <span className="float-right text-muted text-sm">1 day</span>
              </a>
              <div className="dropdown-divider"></div>
              <a href="#" className="dropdown-item dropdown-footer">See All Notifications</a>
            </div>
          </li>

          {/* User Dropdown Menu */}
          <li className="nav-item dropdown">
            <a className="nav-link" data-toggle="dropdown" href="#">
              <i className="far fa-user-circle"></i>
              <span className="ml-2 d-none d-md-inline">{userName}</span>
            </a>
            <div className="dropdown-menu dropdown-menu-lg dropdown-menu-right">
              <a href="#" className="dropdown-item">
                <i className="fas fa-user mr-2"></i> Profile
                <span className="badge badge-secondary float-right">Soon</span>
              </a>
              <div className="dropdown-divider"></div>
              <a href="#" className="dropdown-item">
                <i className="fas fa-cog mr-2"></i> Settings
              </a>
              <div className="dropdown-divider"></div>
              <a href="#" className="dropdown-item" onClick={(e) => { e.preventDefault(); onSignOut(); }}>
                <i className="fas fa-sign-out-alt mr-2"></i> Sign Out
              </a>
            </div>
          </li>

          {/* Control Sidebar Toggle */}
          <li className="nav-item">
            <a className="nav-link" data-widget="control-sidebar" data-slide="true" href="#" role="button">
              <i className="fas fa-th-large"></i>
            </a>
          </li>
        </ul>
      </nav>

      {/* Main Sidebar Container */}
      <aside className="main-sidebar sidebar-dark-primary elevation-4">
        {/* Brand Logo */}
        <a href="#" className="brand-link" style={{ display: 'flex', alignItems: 'center' }}>
          <i className="fas fa-tools brand-image" style={{ marginRight: '8px' }}></i>
          <span className="brand-text font-weight-light">PAT</span>
        </a>

        {/* Sidebar */}
        <div className="sidebar">
          {/* Sidebar user panel */}
          <div className="user-panel mt-3 pb-3 mb-3 d-flex">
            <div className="image">
              <i className="fas fa-user-circle fa-2x text-white"></i>
            </div>
            <div className="info">
              <a href="#" className="d-block">{userName}</a>
            </div>
          </div>

          {/* Sidebar Menu */}
          <nav className="mt-2">
            <ul className="nav nav-pills nav-sidebar flex-column" data-widget="treeview" role="menu" data-accordion="false">
              
              {/* Dashboard */}
              <li className="nav-item">
                <a 
                  href="#" 
                  className={`nav-link ${activeMenu === 'dashboard' ? 'active' : ''}`}
                  onClick={(e) => { e.preventDefault(); setActiveMenu('dashboard'); }}
                >
                  <i className="nav-icon fas fa-tachometer-alt"></i>
                  <p>Dashboard</p>
                </a>
              </li>

              {/* Workload Customisations */}
              <li className="nav-item">
                <a 
                  href="#" 
                  className={`nav-link ${activeMenu === 'workloads' ? 'active' : ''}`}
                  onClick={(e) => { e.preventDefault(); setActiveMenu('workloads'); }}
                >
                  <i className="nav-icon fas fa-cogs"></i>
                  <p>Workload Customisations</p>
                </a>
              </li>

              {/* Certificate Management */}
              <li className="nav-item">
                <a 
                  href="#" 
                  className={`nav-link ${activeMenu === 'certificates' ? 'active' : ''}`}
                  onClick={(e) => { e.preventDefault(); setActiveMenu('certificates'); }}
                >
                  <i className="nav-icon fas fa-certificate"></i>
                  <p>Certificate Management</p>
                </a>
              </li>

              {/* mTLS Management */}
              <li className="nav-item">
                <a 
                  href="#" 
                  className={`nav-link ${activeMenu === 'mtls' ? 'active' : ''}`}
                  onClick={(e) => { e.preventDefault(); setActiveMenu('mtls'); }}
                >
                  <i className="nav-icon fas fa-shield-alt"></i>
                  <p>mTLS Management</p>
                </a>
              </li>

              {/* IAM Management */}
              <li className="nav-item">
                <a 
                  href="#" 
                  className={`nav-link ${activeMenu === 'iam' ? 'active' : ''}`}
                  onClick={(e) => { e.preventDefault(); setActiveMenu('iam'); }}
                >
                  <i className="nav-icon fas fa-robot"></i>
                  <p>IAM (M2M)</p>
                </a>
              </li>

              {/* Divider */}
              <li className="nav-header">SYSTEM</li>

              {/* Users */}
              <li className="nav-item">
                <a 
                  href="#" 
                  className={`nav-link ${activeMenu === 'users' ? 'active' : ''}`}
                  onClick={(e) => { e.preventDefault(); setActiveMenu('users'); }}
                >
                  <i className="nav-icon fas fa-users"></i>
                  <p>Users</p>
                </a>
              </li>

              {/* Settings */}
              <li className="nav-item">
                <a 
                  href="#" 
                  className={`nav-link ${activeMenu === 'settings' ? 'active' : ''}`}
                  onClick={(e) => { e.preventDefault(); setActiveMenu('settings'); }}
                >
                  <i className="nav-icon fas fa-cog"></i>
                  <p>Settings</p>
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </aside>

      {/* Content Wrapper */}
      <div className="content-wrapper">
        {/* Content Header (Page header) */}
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">
                  {activeMenu === 'workloads' && 'Workload Customisations'}
                  {activeMenu === 'certificates' && 'Certificate Management'}
                  {activeMenu === 'mtls' && 'mTLS Management'}
                  {activeMenu === 'iam' && 'IAM Management (M2M)'}
                  {activeMenu === 'users' && 'User Management'}
                  {activeMenu === 'settings' && 'Settings'}
                  {activeMenu === 'dashboard' && 'Dashboard'}
                </h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item"><a href="#">Home</a></li>
                  <li className="breadcrumb-item active">
                    {activeMenu === 'workloads' && 'Workloads'}
                    {activeMenu === 'certificates' && 'Certificates'}
                    {activeMenu === 'mtls' && 'mTLS'}
                    {activeMenu === 'iam' && 'IAM'}
                    {activeMenu === 'users' && 'Users'}
                    {activeMenu === 'settings' && 'Settings'}
                    {activeMenu === 'dashboard' && 'Dashboard'}
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <section className="content">
          <div className="container-fluid">
            {activeMenu === 'workloads' && children}
            {activeMenu === 'certificates' && <CertificateManagement />}
            {activeMenu === 'mtls' && <MtlsLayout />}
            {activeMenu === 'iam' && <IamLayout />}
            {activeMenu === 'users' && <UserManagement />}
            {activeMenu === 'settings' && (
              <div className="card card-primary card-outline">
                <div className="card-header">
                  <h3 className="card-title">
                    <i className="fas fa-cog mr-2"></i>
                    Settings
                  </h3>
                </div>
                <div className="card-body">
                  <p className="text-muted">Platform configuration settings</p>
                </div>
              </div>
            )}
            {activeMenu === 'dashboard' && <Dashboard />}
          </div>
        </section>
      </div>

      {/* Control Sidebar */}
      <aside className="control-sidebar control-sidebar-dark">
        <div className="p-3">
          <h5>Quick Actions</h5>
          <div className="mb-4">
            <button className="btn btn-primary btn-block">
              <i className="fas fa-plus mr-2"></i>
              New Workload
            </button>
            <button className="btn btn-info btn-block">
              <i className="fas fa-sync mr-2"></i>
              Refresh Status
            </button>
          </div>
          <h5>Recent Activity</h5>
          <div className="list-group">
            <a href="#" className="list-group-item list-group-item-action">
              <div className="d-flex w-100 justify-content-between">
                <small>Workload deployed</small>
                <small>5m ago</small>
              </div>
            </a>
            <a href="#" className="list-group-item list-group-item-action">
              <div className="d-flex w-100 justify-content-between">
                <small>Config updated</small>
                <small>1h ago</small>
              </div>
            </a>
          </div>
        </div>
      </aside>

      {/* Footer */}
      <footer className="main-footer">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <strong>PAT - Platform Administration Tool</strong> - Internal Tool
          </div>
          <div className="d-none d-sm-flex align-items-center">
            <img 
              src="/images/logos/logo-footer.png" 
              alt="Company Logo" 
              style={{ maxHeight: '24px', width: 'auto', objectFit: 'contain' }}
              loading="lazy"
              onError={(e) => {
                // Fallback to generic logo, then hide
                const img = e.target as HTMLImageElement;
                if (img.src.includes('logo-footer.png')) {
                  img.src = '/images/logo.png';
                } else {
                  img.style.display = 'none';
                }
              }}
            />
          </div>
        </div>
      </footer>
    </div>
  );
}
